import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ticketStatusesApi } from '../api/ticketStatuses';
import { useCompany } from '../contexts/CompanyContext';
import { registerStatusDefs } from '../components/badges';
import type { TicketStatusDef, StatusCategory } from '../types';

/** Categories that mean "still needs work" — mirrors the backend. */
export const ACTIVE_CATEGORIES: StatusCategory[] = ['new', 'open', 'waiting_customer', 'waiting_internal'];
export const ACTIVE_CATEGORY_CSV = ACTIVE_CATEGORIES.join(',');

/**
 * The company's ticket status definitions. Also feeds the StatusBadge
 * registry so badges anywhere pick up custom labels/colors.
 */
export function useTicketStatuses() {
  const { currentCompany } = useCompany();
  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const { data, isLoading } = useQuery({
    queryKey: ['ticket-statuses', companyId],
    queryFn: () => ticketStatusesApi.list(),
    enabled: !!currentCompany,
    staleTime: 5 * 60 * 1000,
  });

  const statuses = useMemo(() => data ?? [], [data]);

  useEffect(() => {
    if (statuses.length > 0) registerStatusDefs(statuses);
  }, [statuses]);

  const active = useMemo(() => statuses.filter(s => s.isActive), [statuses]);
  const byKey = useMemo(() => new Map(statuses.map(s => [s.key, s])), [statuses]);

  return {
    statuses,
    active,
    byKey,
    isLoading,
    categoryOf: (key: string): StatusCategory | undefined => byKey.get(key)?.category,
    labelOf: (key: string): string => byKey.get(key)?.label ?? key.replace(/_/g, ' '),
  };
}

export type { TicketStatusDef };
